import React, { useState, useMemo, useRef, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FiSearch, FiChevronDown, FiClock, FiStar,
  FiUsers, FiArrowRight, FiCopy,
  FiSettings, FiZap, FiFilter, FiCheck, FiGrid,
} from 'react-icons/fi';
import MomentumLogo from '../../assets/images/MomentumLogo.png';
import PopularToolsLogo from '../../assets/images/Popular Tools.svg';
import RecentLogo from '../../assets/images/Recent.svg';
import DataEnrichmentLogo from '../../assets/images/Data Enrichment.svg';
import AIScanLogo from '../../assets/images/AI Scan and Extract Data.svg';
import LinqsLogo from '../../assets/images/Linqs.svg';
import SettingsLogo from '../../assets/images/Settings.svg';
import type { Module } from './MarketplacePage';
import { getModuleIcon, getCatColor, isCustomSvgIcon } from './moduleIconUtils';
import { matchesPublisher, publisherOptions } from '../mytools/publisherFilter';
import '../../styles/ToolboxLanding.css';

/* ─── User count API ─── */

interface UserCountEntry { id: number; api: string; count: number; }

const API_TO_MODULE: Array<[string, string]> = [
  ['nowcerts coi',          'certificate of insurance'],
  ['commission statement',  'commission'],
  ['driver license scan',   'driver license'],
  ['invoice scan',          'invoice'],
  ['lossrunscan',           'loss run'],
  ['mvrscan',               'mvr'],
  ['upsell indicator',      'upsell'],
  ['naics classification',  'naics'],
  ['valchoice classification', 'classification'],
  ['rateappetite',          'rate risk appetite'],
  ['rate risk appetite',    'rate risk appetite'],
  ['appetite',              'rate risk appetite'],
  ['homeowners',            'home owner'],
  ['fenrispl',              'data enrichment personal'],
  ['fenrisdav',             'data enrichment driver'],
  ['policy comparison',     'policy comparison'],
  ['dataquality',           'data quality'],
  ['sonant',                'sonant'],
  ['mtmscan',               'mtm scan'],
  ['mtm scan',              'mtm scan'],
  ['motor truck mutual',    'mtm scan'],
  ['pf smart scan',         'premfi'],
  ['nowcerts smart scan',   'acord and dec page'],
  ['vehicle prefill',       'vehicle prefill'],
  ['dataenrichment commercial', 'data enrichment commercial'],
  ['e-signature',           'rabbit sign'],
];

const TOOL_COUNT_OFFSET: Record<string, number> = {
  'commission':                150,
  'certificate of insurance':  150,
  'policy comparison':         150,
  'driver license':            150,
  'invoice':                   150,
  'upsell':                    150,
  'loss run':                  150,
  'mvr':                       150,
  'naics':                     150,
  'home owner':                150,
  'data enrichment personal':  150,
  'data enrichment driver':    150,
  'data quality':              150,
  'rate risk appetite':        150,
  'sonant':                    150,
  'mtm scan':                  150,
  'premfi':                    150,
  'acord and dec page':        150,
  'vehicle prefill':           150,
  'data enrichment commercial': 150,
  'rabbit sign':               150,
};

function buildCountMap(entries: UserCountEntry[]): Record<string, number> {
  const map: Record<string, number> = {};
  for (const entry of entries) {
    const apiLower = entry.api.toLowerCase();
    for (const [apiKey, moduleKey] of API_TO_MODULE) {
      if (apiLower.includes(apiKey)) {
        map[moduleKey] = (map[moduleKey] || 0) + entry.count;
        break;
      }
    }
  }
  for (const [key, offset] of Object.entries(TOOL_COUNT_OFFSET)) {
    if (map[key] !== undefined) map[key] += offset;
  }
  if (map['policy comparison'] !== undefined) {
    map['quote analyzer'] = map['policy comparison'];
  }
  if (map['classification'] !== undefined) {
    map['carrier rating'] = map['classification'] + 150;
  }
  return map;
}

function getCountForName(name: string, countMap: Record<string, number>): number {
  const lower = name.toLowerCase();
  for (const [key, val] of Object.entries(countMap)) {
    if (lower.includes(key)) return val;
  }
  return 0;
}

/* ─── localStorage helpers ─── */
const RECENT_KEY = 'tbx_recent_tools';

export function recordToolOpen(moduleId: string): void {
  try {
    const stored: Array<{ id: string; at: string }> = JSON.parse(
      localStorage.getItem(RECENT_KEY) || '[]',
    );
    const next = [
      { id: moduleId, at: new Date().toISOString() },
      ...stored.filter((r) => r.id !== moduleId),
    ].slice(0, 8);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {}
}

function getRecentMap(): Record<string, string> {
  try {
    const items: Array<{ id: string; at: string }> = JSON.parse(
      localStorage.getItem(RECENT_KEY) || '[]',
    );
    return Object.fromEntries(items.map((r) => [r.id, r.at]));
  } catch {
    return {};
  }
}

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${Math.max(1, mins)} min${mins !== 1 ? 's' : ''} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days !== 1 ? 's' : ''} ago`;
}

/* ─── Star display ─── */
const StarDisplay: React.FC<{ rating: number }> = ({ rating }) => (
  <span className="tbx-stars">
    {[1, 2, 3, 4, 5].map((n) => (
      <FiStar
        key={n}
        size={11}
        className={n <= Math.round(rating) ? 'tbx-star--on' : 'tbx-star--off'}
      />
    ))}
  </span>
);

/* ─── Category section metadata ─── */
const SECTION_ORDER = [
  'AI Scan and Extract Data',
  'Third Party Data Enrichment Integrations',
  'Linqs',
  'Widgets',
  'Community Skills',
  'Community Agents',
  'Other Features',
];

const CAT_ICON_MAP: Record<string, React.ComponentType<{ size?: number }>> = {
  'Linqs':         FiCopy,
  'Widgets':       FiGrid,
  'Community Skills': FiZap,
  'Community Agents': FiUsers,
  'Other Features': FiSettings,
};

const CAT_LOGO_MAP: Record<string, string> = {
  'AI Scan and Extract Data':                 AIScanLogo,
  'Third Party Data Enrichment Integrations': DataEnrichmentLogo,
  'Linqs':                                    LinqsLogo,
  'Other Features':                           SettingsLogo,
};

const CAT_LABEL: Record<string, string> = {
  'AI Scan and Extract Data':                 'AI Scan and Extract Data',
  'Third Party Data Enrichment Integrations': 'Third Party Data Enrichment Integrations',
  'Linqs':                                    'Linqs',
  'Widgets':                                  'Widgets',
  'Community Skills':                         'Community Skills',
  'Community Agents':                         'Community Agents',
  'Other Features':                           'Other Features',
};

/* ─── Hardcoded tool ratings (out of 5) ─── */
const TOOL_RATINGS: Array<[string, number]> = [
  ['certificate of insurance', 4],
  ['commission scan',          4],
  ['driver license',           4],
  ['invoice scan',             4],
  ['loss run',                 4],
  ['mvr',                      5],
  ['upsell',                   4],
  ['classification',           4],
  ['naics',                    4],
  ['home owner',               4],
  ['policy comparison',        4],
  ['quote analyzer',           4],
  ['sonant',                   5],
  ['data quality',             4],
  ['rate risk',                4],
  ['mtm',                      4],
  ['acord',                    4],
];

function getToolRating(name: string): number {
  const lower = name.toLowerCase();
  for (const [key, rating] of TOOL_RATINGS) {
    if (lower.includes(key)) return rating;
  }
  return 4.0;
}

/* ─── Tool card (category sections + popular) ─── */
interface ToolCardProps {
  module: Module;
  onClick: () => void;
  index?: number;
  userCount?: number;
}

const ToolCard: React.FC<ToolCardProps> = ({ module, onClick, index = 0, userCount = 0 }) => {
  const Icon = getModuleIcon(module);
  const catColor = getCatColor(module.category);
  const rating = getToolRating(module.name);
  const isSvg = isCustomSvgIcon(module);
  return (
    <motion.div
      className="tbx-tool-card"
      onClick={onClick}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04, duration: 0.22 }}
      whileHover={{ y: -3, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
    >
      {/* Top row */}
      <div className="tbx-tool-card__top">
        <div
          className="tbx-tool-card__icon-wrap"
          style={isSvg
            ? { background: '#ffffff', border: '1px solid rgba(0,0,0,0.08)', padding: 5, overflow: 'hidden' }
            : { background: catColor + '1a', color: catColor }}
        >
          <Icon size={22} />
        </div>
        <div className="tbx-tool-card__title-area">
          <h3 className="tbx-tool-card__name">{module.name}</h3>
          {module.publisher && (
            <span className="tbx-tool-card__publisher" title={`Published by ${module.publisher}`}>
              by {module.publisher}{module.publisherVerified && <FiCheck size={11} className="tbx-tool-card__verified" aria-label="Verified publisher" />}
            </span>
          )}
          {(module.sourceToolId ?? module.widgetToolId) != null && (
            <span style={{ display: 'inline-block', marginTop: 2, padding: '1px 7px', borderRadius: 999, fontSize: 10.5, fontWeight: 600, background: 'rgba(162,0,255,0.12)', color: '#A200FF', border: '1px solid rgba(162,0,255,0.3)' }}>
              {module.widgetToolId != null ? 'Widget' : module.moduleType === 'Agent' ? 'Community Agent' : 'Community Skill'}
            </span>
          )}
        </div>
      </div>

      {/* Description */}
      <p className="tbx-tool-card__desc">{module.description}</p>

      {/* Footer */}
      <div className="tbx-tool-card__footer">
        <StarDisplay rating={rating} />
        {userCount > 0 && (
          <span className="tbx-tool-card__users">
            <FiUsers size={11} />
            {userCount}
          </span>
        )}
        <button className="tbx-tool-card__explore">
          Explore <FiArrowRight size={11} />
        </button>
      </div>
    </motion.div>
  );
};

/* ─── Recently-used card ─── */
interface RecentCardProps {
  module: Module;
  openedAt: string;
  onClick: () => void;
  index?: number;
}

const RecentCard: React.FC<RecentCardProps> = ({ module, openedAt, onClick, index = 0 }) => {
  const Icon = getModuleIcon(module);
  const catColor = getCatColor(module.category);
  const isSvg = isCustomSvgIcon(module);

  return (
    <motion.div
      className="tbx-recent-card"
      onClick={onClick}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.2 }}
      whileHover={{ y: -3, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
    >
      <div className="tbx-tool-card__top">
        <div
          className="tbx-tool-card__icon-wrap"
          style={isSvg
            ? { background: '#ffffff', border: '1px solid rgba(0,0,0,0.08)', padding: 5, overflow: 'hidden' }
            : { background: catColor + '1a', color: catColor }}
        >
          <Icon size={20} />
        </div>
        <div className="tbx-tool-card__title-area">
          <h3 className="tbx-tool-card__name">{module.name}</h3>
          {module.publisher && (
            <span className="tbx-tool-card__publisher" title={`Published by ${module.publisher}`}>
              by {module.publisher}{module.publisherVerified && <FiCheck size={11} className="tbx-tool-card__verified" aria-label="Verified publisher" />}
            </span>
          )}
          {(module.sourceToolId ?? module.widgetToolId) != null && (
            <span style={{ display: 'inline-block', marginTop: 2, padding: '1px 7px', borderRadius: 999, fontSize: 10.5, fontWeight: 600, background: 'rgba(162,0,255,0.12)', color: '#A200FF', border: '1px solid rgba(162,0,255,0.3)' }}>
              {module.widgetToolId != null ? 'Widget' : module.moduleType === 'Agent' ? 'Community Agent' : 'Community Skill'}
            </span>
          )}
        </div>
      </div>
      <p className="tbx-tool-card__desc">{module.description}</p>
      <div className="tbx-recent-card__time">
        <FiClock size={11} />
        {timeAgo(openedAt)}
      </div>
    </motion.div>
  );
};

/* ─── Allowlist: only these tools are shown on the landing page ─── */
const LANDING_TOOL_SUBSTRINGS = [
  'certificate of insurance',
  'commission scan',
  'driver license',
  'invoice scan',
  'loss run',
  'mvr scan',
  'upsell indicator',
  'classification codes',
  'naics',
  'home owner',
  'fenris personal line',
  'data enrichment personal',
  'fenris driver',
  'data enrichment driver',
  'data enrichment commercial',
  'policy comparison',
  'quote analyzer',
  'sonant',
  'linqs e-signature',
  'linqs-e-signature',
  'data quality',
  'rate risk appetite',
  'carrier rating',
  'mtm scan',
  'motor truck mutual',
  'my carrier finder',
  'vehicle prefill',
  'acord and dec pages scan',
  'dec pages scan',
  'premfi',
  'pfa scan',
];

function isAllowedTool(name: string): boolean {
  const lower = name.toLowerCase();
  return LANDING_TOOL_SUBSTRINGS.some((s) => lower.includes(s));
}

/* ─── Main component ─── */
export interface ToolboxLandingProps {
  modules: Module[];
  isLoading: boolean;
  onModuleClick: (module: Module) => void;
}

const ToolboxLanding: React.FC<ToolboxLandingProps> = ({ modules, isLoading, onModuleClick }) => {
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('All Categories');
  const [catOpen, setCatOpen] = useState(false);
  const [activePublisher, setActivePublisher] = useState('All Publishers');
  const [pubOpen, setPubOpen] = useState(false);
  const pubRef = useRef<HTMLDivElement>(null);
  const [userCountMap, setUserCountMap] = useState<Record<string, number>>({});
  const [userCountLoading, setUserCountLoading] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
      if (pubRef.current && !pubRef.current.contains(e.target as Node)) {
        setPubOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!(process.env.REACT_APP_UserService_Url || '')) return;
    setUserCountLoading(true);
    const token = localStorage.getItem('toolboxToken') || '';
    const config = {
      method: 'get',
      url: `${(process.env.REACT_APP_UserService_Url || '')}/api/user/usercount`,
      headers: { Authorization: `Bearer ${token}` },
    };
    axios(config)
      .then((response: any) => {
        setUserCountMap(buildCountMap(response.data as UserCountEntry[]));
        setUserCountLoading(false);
      })
      .catch(() => {
        // Usage counts are a supplementary display detail (see ToolCard's userCount
        // badge, hidden when 0) — a missing/stale toolboxToken here must not disrupt
        // navigation or look like an auth failure.
        setUserCountLoading(false);
      });
  }, []);

  /* Apply landing allowlist first — name substring OR explicit moduleId */
  const ALLOWED_MODULE_IDS = new Set(['linqs-e-signature', 'mtm-scan', 'mvr', 'acord-dec-pages-scan', 'premfi-pfa-scan']);
  const allowedModules = useMemo(
    // A widget-backed module (marketplace_widget_bridge_migration.sql) always shows — its name
    // can't be hand-listed here in advance the way a fixed first-party tool's can.
    () => modules.filter((m) => isAllowedTool(m.name) || ALLOWED_MODULE_IDS.has(m.moduleId) || (m.sourceToolId ?? m.widgetToolId) != null),
    [modules],
  );

  const handleClick = (module: Module) => {
    recordToolOpen(module.moduleId);
    onModuleClick(module);
  };

  /* Recently used */
  const recentMap = useMemo(() => getRecentMap(), []);
  const recentModules = useMemo(() => {
    const entries = Object.entries(recentMap).sort((a, b) =>
      new Date(b[1]).getTime() - new Date(a[1]).getTime(),
    );
    return entries
      .map(([id, at]) => {
        const mod = allowedModules.find((m) => m.moduleId === id);
        return mod ? { mod, at } : null;
      })
      .filter((x): x is { mod: Module; at: string } => x !== null)
      .slice(0, 4);
  }, [allowedModules, recentMap]);

  /* Category dropdown options */
  const categoryOptions = useMemo(() => {
    const cats = Array.from(new Set(allowedModules.map((m) => m.category).filter(Boolean)));
    return ['All Categories', ...cats];
  }, [allowedModules]);

  /* Publisher dropdown options */
  const publisherChoices = useMemo(
    () => ['All Publishers', ...publisherOptions(allowedModules.map((m) => m.publisher))],
    [allowedModules],
  );

  /* Filtered modules */
  const filtered = useMemo(() => {
    let list = [...allowedModules];
    if (activeCat !== 'All Categories') list = list.filter((m) => m.category === activeCat);
    if (activePublisher !== 'All Publishers') list = list.filter((m) => matchesPublisher(m.publisher, activePublisher));
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.description.toLowerCase().includes(q) ||
          m.tags.some((t) => t.toLowerCase().includes(q)),
      );
    }
    return list;
  }, [allowedModules, activeCat, activePublisher, search]);

  /* Most popular: top 4 by satisfaction */
  const popularModules = useMemo(() => {
    return [...filtered]
      .sort((a, b) => (parseFloat(b.satisfaction) || 0) - (parseFloat(a.satisfaction) || 0))
      .slice(0, 4);
  }, [filtered]);

  /* Category sections */
  const categorySections = useMemo(() => {
    const order = activeCat !== 'All Categories'
      ? [activeCat]
      : SECTION_ORDER;
    return order
      .map((cat) => ({ cat, items: filtered.filter((m) => m.category === cat) }))
      .filter(({ items }) => items.length > 0);
  }, [filtered, activeCat]);

  /* Skeleton cards */
  const SkeletonCard = () => (
    <div className="tbx-skeleton">
      <div className="tbx-skeleton__icon" />
      <div className="tbx-skeleton__line tbx-skeleton__line--w70" />
      <div className="tbx-skeleton__line tbx-skeleton__line--w55" />
      <div className="tbx-skeleton__line tbx-skeleton__line--w40" />
    </div>
  );

  return (
    <div className="tbx-landing">
      {/* ── Top search bar ── */}
      <div className="tbx-topbar">
        {/* Left: branding */}
        <div className="tbx-topbar__left">
          <div className="tbx-topbar__icon-wrap">
            <img src={MomentumLogo} alt="Momentum" className="tbx-topbar__logo" />
          </div>
          <div>
            <div className="tbx-topbar__title">Toolbox</div>
            <div className="tbx-topbar__sub">Discover skills and agents built for insurance agencies</div>
          </div>
        </div>

        {/* Right: search + filter */}
        <div className="tbx-topbar__right">
          <div className="tbx-hero__search-wrap">
            <FiSearch size={16} className="tbx-hero__search-icon" />
            <input
              className="tbx-hero__search-input"
              type="text"
              placeholder="Search for tools..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="tbx-cat-dropdown" ref={catRef}>
            <button
              className="tbx-cat-trigger"
              onClick={() => setCatOpen((o) => !o)}
            >
              <FiFilter size={14} className="tbx-cat-trigger__icon" />
              <span className="tbx-cat-trigger__label">{activeCat}</span>
              <FiChevronDown
                size={14}
                className={`tbx-cat-trigger__chevron${catOpen ? ' tbx-cat-trigger__chevron--open' : ''}`}
              />
            </button>
            {catOpen && (
              <div className="tbx-cat-panel">
                {categoryOptions.map((c) => (
                  <button
                    key={c}
                    className={`tbx-cat-option${activeCat === c ? ' tbx-cat-option--active' : ''}`}
                    onClick={() => { setActiveCat(c); setCatOpen(false); }}
                  >
                    <span>{c}</span>
                    {activeCat === c && <FiCheck size={13} className="tbx-cat-option__check" />}
                  </button>
                ))}
              </div>
            )}
          </div>
          {publisherChoices.length > 2 && (
            <div className="tbx-cat-dropdown" ref={pubRef}>
              <button
                className="tbx-cat-trigger tbx-cat-trigger--publisher"
                onClick={() => setPubOpen((o) => !o)}
                aria-label="Filter by publisher"
              >
                <FiUsers size={14} className="tbx-cat-trigger__icon" />
                <span className="tbx-cat-trigger__label">{activePublisher}</span>
                <FiChevronDown
                  size={14}
                  className={`tbx-cat-trigger__chevron${pubOpen ? ' tbx-cat-trigger__chevron--open' : ''}`}
                />
              </button>
              {pubOpen && (
                <div className="tbx-cat-panel">
                  {publisherChoices.map((p) => (
                    <button
                      key={p}
                      className={`tbx-cat-option${activePublisher === p ? ' tbx-cat-option--active' : ''}`}
                      onClick={() => { setActivePublisher(p); setPubOpen(false); }}
                    >
                      <span>{p}</span>
                      {activePublisher === p && <FiCheck size={13} className="tbx-cat-option__check" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="tbx-body">

        {/* Recently Used */}
        {recentModules.length > 0 && !search && activeCat === 'All Categories' && activePublisher === 'All Publishers' && (
          <section className="tbx-section">
            <h2 className="tbx-section__heading">
              <span className="tbx-section__heading-icon tbx-section__heading-icon--img">
                <img src={RecentLogo} alt="Recent" className="tbx-section__heading-logo" />
              </span>
              Recently Used Tools
            </h2>
            <div className="tbx-recent-grid">
              {recentModules.map(({ mod, at }, i) => (
                <RecentCard key={mod.moduleId} module={mod} openedAt={at} onClick={() => handleClick(mod)} index={i} />
              ))}
            </div>
          </section>
        )}

        {/* Most Popular */}
        {!search && activeCat === 'All Categories' && (
          <section className="tbx-section">
            <h2 className="tbx-section__heading">
              <span className="tbx-section__heading-icon tbx-section__heading-icon--img">
                <img src={PopularToolsLogo} alt="Popular Tools" className="tbx-section__heading-logo" />
              </span>
              Most Popular &amp; Powerful Solutions
            </h2>
            {isLoading ? (
              <div className="tbx-popular-grid">
                {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
              </div>
            ) : (
              <div className="tbx-popular-grid">
                {popularModules.map((mod, i) => (
                  <ToolCard key={mod.moduleId} module={mod} onClick={() => handleClick(mod)} index={i} userCount={getCountForName(mod.name, userCountMap)} />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Category sections */}
        {isLoading ? (
          <section className="tbx-section">
            <div className="tbx-skeleton__heading" />
            <div className="tbx-grid">
              {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
          </section>
        ) : (
          categorySections.map(({ cat, items }) => {
            const CatIcon = CAT_ICON_MAP[cat] ?? FiZap;
            const catLogo = CAT_LOGO_MAP[cat];
            const catColor = getCatColor(cat);
            const label = CAT_LABEL[cat] ?? cat;
            return (
              <section key={cat} className="tbx-section">
                <h2 className="tbx-section__heading">
                  {catLogo ? (
                    <span className="tbx-section__heading-icon tbx-section__heading-icon--img">
                      <img src={catLogo} alt={label} className="tbx-section__heading-logo" />
                    </span>
                  ) : (
                    <span className="tbx-section__heading-icon" style={{ color: catColor }}>
                      <CatIcon size={18} />
                    </span>
                  )}
                  {label}
                </h2>
                <div className="tbx-grid">
                  {items.map((mod, i) => (
                    <ToolCard key={mod.moduleId} module={mod} onClick={() => handleClick(mod)} index={i} userCount={getCountForName(mod.name, userCountMap)} />
                  ))}
                </div>
              </section>
            );
          })
        )}

        {!isLoading && filtered.length === 0 && (
          <div className="tbx-empty">
            <FiSearch size={32} />
            <p>No tools found for &ldquo;{search}&rdquo;</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolboxLanding;

